import { useRef, useState } from "react";
import type { PlotPurchaseResponse, RealEstateListing } from "@townsy/contracts";
import { postPlotPurchase } from "../../api/endpoints";
import { formatCoins } from "../../lib/wallet";
import { purchaseAttempts, type PurchaseAttempt, type PurchaseOutcome } from "../../realEstate/purchase";
import { Dialog } from "../Dialog";
import { useTestPrices } from "../testPrices";

/**
 * Purchase review for a residential plot paid with coins (docs/01 section 6 "Purchase review",
 * docs/04 sections 6 and 9). One confirmation = one Idempotency-Key; "Try again" resends the SAME
 * key and body, so a lost response can never spend twice. A stale price or a plot bought by
 * someone else ends this attempt; the visitor reviews the refreshed listing and confirms anew.
 */
export function PurchaseDialog({
  listing,
  coinsAvailable,
  onClose,
  onPurchased,
  onStale,
}: {
  listing: RealEstateListing;
  coinsAvailable: number;
  onClose(): void;
  onPurchased(response: PlotPurchaseResponse): void;
  /** Listings/balances are out of date (price changed, sold, limit reached…): refresh them. */
  onStale(): void;
}) {
  const price = listing.offer.coins ?? 0;
  const testPrices = useTestPrices();
  const attemptRef = useRef<PurchaseAttempt | null>(null);
  const [state, setState] = useState<"review" | "submitting" | "failed">("review");
  const [failure, setFailure] = useState<Extract<PurchaseOutcome, { ok: false }> | null>(null);
  const after = coinsAvailable - price;

  const submit = async () => {
    // Created once, at the first confirmation; every retry of this confirmation (even after the
    // dialog was closed and reopened while unresolved) reuses the same key and body.
    if (!attemptRef.current) attemptRef.current = purchaseAttempts.forListing(listing.propertyId, price);
    const attempt = attemptRef.current;
    setState("submitting");
    setFailure(null);
    const outcome = await attempt.submit(postPlotPurchase);
    if (outcome.ok) {
      purchaseAttempts.settle(attempt);
      onPurchased(outcome.response);
      return;
    }
    setFailure(outcome);
    setState("failed");
    if (!outcome.retryable) {
      // This confirmation is over: a new review starts a new attempt with a new key.
      purchaseAttempts.settle(attempt);
      attemptRef.current = null;
      if (outcome.refresh) onStale();
    }
  };

  const submitting = state === "submitting";
  const canRetry = state === "failed" && failure?.retryable;
  const ended = state === "failed" && failure && !failure.retryable;

  return (
    <Dialog
      title="Confirm plot purchase"
      onClose={() => {
        if (!submitting) onClose();
      }}
      describedBy="purchase-summary"
      footer={
        <>
          {ended ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => void submit()} data-testid="purchase-confirm">
                {submitting ? "Buying…" : canRetry ? "Try again" : `Buy for ${formatCoins(price)}`}
              </button>
              <button type="button" className="btn" disabled={submitting} onClick={onClose}>
                Cancel
              </button>
            </>
          )}
        </>
      }
    >
      <div className="stack" id="purchase-summary" data-testid="purchase-dialog">
        <dl className="kv">
          <dt>Plot</dt>
          <dd>
            <strong>{listing.address}</strong>
            <div className="small muted">{listing.street}</div>
          </dd>
          <dt>Price</dt>
          <dd className="price">
            <strong>{formatCoins(price)}</strong>
          </dd>
          <dt>Paid with</dt>
          <dd>Coins (your coins: {formatCoins(coinsAvailable)})</dd>
          <dt>After purchase</dt>
          <dd>{formatCoins(Math.max(0, after))}</dd>
          <dt>You get</dt>
          <dd>The plot and a starting tent</dd>
        </dl>
        <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            <strong>Homes can never be taken over by other players.</strong>
          </li>
          <li>Your home starts as a tent and can be upgraded later.</li>
          <li>Coin purchases are final: coins spent on a plot are not refunded{testPrices ? " (development test prices)" : ""}.</li>
          <li>Coins and dollar Credit are separate: this purchase uses coins only.</li>
        </ul>
        {failure ? (
          <div className={`notice ${failure.retryable ? "notice-warning" : "notice-danger"}`} role="alert" data-testid="purchase-error">
            {failure.message}
          </div>
        ) : null}
        {submitting ? (
          <p className="small muted" role="status">
            Confirming with Townsy…
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
